<template>
  <div class="login-container">
    <div class="background-image" ref="backgroundImage">
      <div class="welcome-text">
        <p>您好，</p>
        <p>欢迎登录会议申请助手</p>
      </div>
    </div>


    <div class="login-content">


      <!-- 登录表单容器 -->
      <div class="form-container">
        <!-- 登录表单 -->
        <van-form v-if="!showPasswordChange" @submit="onSubmit" class="login-form">
          <div class="login-inputs">
            <div class="field-container">
              <div class="field-label">手机号</div>
              <van-field v-model="username" name="username" placeholder="请输入手机号"
                :rules="[{ required: true, message: '请输入手机号' }]" class="login-field" />
            </div>
            <div class="field-container">
              <div class="field-label">{{ isSmsLoginMode ? '验证码' : '密码' }}</div>
              <!-- 密码输入框 -->
              <van-field v-if="!isSmsLoginMode" v-model="password" type="password" name="password" placeholder="请输入密码"
                :rules="[{ required: true, message: '请输入密码' }]" class="login-field" />
              <!-- 验证码输入框 -->
              <van-field v-else v-model="smsCode" name="smsCode" placeholder="请输入验证码"
                :rules="[{ required: true, message: '请输入验证码' }]" class="login-field">
                <template #button>
                  <van-button size="small" type="primary" color="#145294" :disabled="countdown > 0 || loading"
                    @click="sendSmsCode" style="font-size: 14px; height: 32px; padding: 0 12px;">
                    {{ countdown > 0 ? `${countdown}s后重新获取` : '发送验证码' }}
                  </van-button>
                </template>
              </van-field>
            </div>
          </div>

          <div class="forgot-password-container">
            <a href="javascript:;" class="forgot-password" @click="handleForgotPassword">忘记密码？</a>
          </div>

          <div class="agreement-container">
            <van-checkbox v-model="agreed" class="agreement-checkbox">
              同意并阅读《用户协议》和《隐私协议》
            </van-checkbox>
          </div>

          <div class="login-buttons">
            <van-button round block type="primary" color="#145294" native-type="submit" :loading="loading"
              class="login-button">
              {{ loading ? '登录中...' : '登录' }}
            </van-button>

            <van-button type="primary" native-type="submit" color="#145294" @click="toggleLoginMode"
              class="sms-login-button">
              {{ isSmsLoginMode ? '密码登录' : '短信登录' }}
            </van-button>
          </div>
        </van-form>

        <!-- 修改密码表单 -->
        <van-form v-else @submit="onChangePassword">
          <van-cell-group inset>
            <van-field v-model="passwordForm.phone" name="phone" label="手机号" placeholder="请输入手机号"
              :rules="[{ required: true, message: '请输入手机号' }]" />
            <van-field v-model="passwordForm.oldPassword" type="password" name="oldPassword" label="原密码"
              placeholder="请输入原密码" :rules="[{ required: true, message: '请输入原密码' }]" />
            <van-field v-model="passwordForm.newPassword" type="password" name="newPassword" label="新密码"
              placeholder="请输入新密码" :rules="[{ required: true, message: '请输入新密码' }]" />
            <van-field v-model="passwordForm.confirmPassword" type="password" name="confirmPassword" label="确认新密码"
              placeholder="请再次输入新密码"
              :rules="[{ required: true, message: '请确认新密码' }, { validator: validatePassword, message: '两次输入的密码不一致' }]" />
          </van-cell-group>
          <div style="margin: 16px;">
            <van-button round block type="primary" color="#145294" native-type="submit" :loading="loading">
              {{ loading ? '修改中...' : '修改密码' }}
            </van-button>
          </div>
          <div style="text-align: center; margin-top: 16px;">
            <van-button type="text" size="small" color="#145294" @click="togglePasswordChange">
              返回登录
            </van-button>
          </div>
        </van-form>
      </div>
    </div>
    <!-- ICP备案号 -->
    <div class="login-icp">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
        浙ICP备2026001625号
      </a>
    </div>
  </div>
</template>

<script>
import { Form, Field, CellGroup, Button, showSuccessToast, showFailToast, showNotify, Checkbox } from 'vant';
import axios from '@/api/axios';

export default {
  name: 'Login',
  components: {
    VanForm: Form,
    VanField: Field,
    VanCellGroup: CellGroup,
    VanButton: Button,
    VanCheckbox: Checkbox
  },
  mounted() {
    // 动态设置背景图片
    if (this.$refs.backgroundImage) {
      this.$refs.backgroundImage.style.backgroundImage = `url('/assets/login/login.png')`;
    }
    this.isMounted = true;
  },
  beforeUnmount() {
    this.isMounted = false;
    // 清理定时器
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  },
  data() {
    return {
      username: '',
      phone: '',
      password: '', // 默认密码
      loading: false,
      showPasswordChange: false,
      isSmsLoginMode: false, // 控制是否处于短信登录模式
      agreed: false,
      smsCode: '', // 短信验证码
      countdown: 0, // 倒计时秒数
      timer: null, // 倒计时定时器
      isMounted: false, // 组件挂载状态跟踪
      passwordForm: {
        phone: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
    };
  },
  methods: {
    async onSubmit() {
      if (!this.agreed) {
        showNotify({ type: 'warning', message: '请先阅读并同意用户协议和隐私协议' });
        return;
      }

      this.loading = true;
      try {
        // 验证手机号格式
        if (!/^1\d{10}$/.test(this.username)) {
          showFailToast('手机号非法');
          return;
        }

        if (this.isSmsLoginMode) {
          // 短信登录：直接调用验证码验证接口，根据API文档，该接口应该返回登录成功的用户信息
          const verifyResponse = await axios.post('/api/sms/verify-code', {
            phone: this.username,
            code: this.smsCode
          });

          if (this.isMounted && verifyResponse.data.success) {
            // 验证码验证成功，即登录成功
            const userInfo = verifyResponse.data.data;
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
            showSuccessToast('登录成功');
            this.$router.push('/home');
          } else if (this.isMounted) {
            showFailToast(verifyResponse.data.message || '验证码验证失败');
          }
        } else {
          // 密码登录
          const loginData = {
            phone: this.username,
            password: this.password
          };

          const response = await axios.post('/api/liaison-officers/login', loginData);

          if (this.isMounted && response.data.success) {
            // 登录成功，保存用户信息
            const userInfo = response.data.data;
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
            showSuccessToast('登录成功');
            this.$router.push('/home');
          } else if (this.isMounted) {
            showFailToast(response.data.message || '登录失败');
          }
        }
      } catch (error) {
        if (this.isMounted) {
          showFailToast(error.response?.data?.message || '登录失败');
        }
      } finally {
        if (this.isMounted) {
          this.loading = false;
        }
      }
    },

    // 切换登录模式（密码/短信）
    toggleLoginMode() {
      this.isSmsLoginMode = !this.isSmsLoginMode;
      // 切换时清空对应的输入框
      if (this.isSmsLoginMode) {
        this.password = '';
      } else {
        this.smsCode = '';
      }
    },
    // 切换登录和修改密码表单
    togglePasswordChange() {
      this.showPasswordChange = !this.showPasswordChange;
      // 切换时清空表单数据
      if (this.showPasswordChange) {
        this.username = '';
        this.password = '';
      } else {
        this.passwordForm = {
          phone: '',
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        };
      }
    },
    // 验证密码一致性
    validatePassword(value) {
      return value === this.passwordForm.newPassword;
    },
    // 忘记密码
    handleForgotPassword() {
      showNotify({
        type: 'warning',
        message: '暂不支持，联系管理员重置',
        duration: 3000
      });
    },
    // 发送短信验证码
    async sendSmsCode() {
      // 验证手机号格式
      if (!/^1\d{10}$/.test(this.username)) {
        showNotify({ type: 'warning', message: '请输入正确的手机号' });
        return;
      }

      this.loading = true;
      try {
        const response = await axios.post('/api/sms/send-verification-code', {
          phone: this.username
        });

        if (this.isMounted && response.data.success) {
          showSuccessToast('验证码发送成功');
          // 开始倒计时
          this.countdown = 60;
          this.startCountdown();
        } else if (this.isMounted) {
          showFailToast(response.data.message || '验证码发送失败');
        }
      } catch (error) {
        if (this.isMounted) {
          showFailToast(error.response?.data?.message || '验证码发送失败');
        }
      } finally {
        if (this.isMounted) {
          this.loading = false;
        }
      }
    },

    // 开始倒计时
    startCountdown() {
      if (this.timer) {
        clearInterval(this.timer);
      }

      this.timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.timer);
        }
      }, 1000);
    },
    // 修改密码
    async onChangePassword() {
      this.loading = true;
      try {
        const response = await axios.post('/api/liaison-officers/change-password', {
          phone: this.passwordForm.phone,
          oldPassword: this.passwordForm.oldPassword,
          newPassword: this.passwordForm.newPassword
        });

        if (response.data.success) {
          showSuccessToast('密码修改成功');
          // 修改成功后切换回登录表单
          this.togglePasswordChange();
        } else {
          showFailToast(response.data.message || '密码修改失败');
        }
      } catch (error) {
        console.error('修改密码失败:', error);
        showFailToast(error.response?.data?.message || '密码修改失败');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.icp-footer {
  text-align: center;
  padding: 20px 0 10px;
  font-size: 12px;
  color: #999;
}

.icp-footer a {
  color: #999;
  text-decoration: none;
}

.login-container {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}

.background-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 30%;
  /* 只显示上三分之一 */
  background-size: cover;
  background-position: center;
  z-index: 0;
}

.login-content {
  position: absolute;
  top: calc(33% - 80px);
  left: 0;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
}

.welcome-text {
  text-align: left;
  margin-left: 50px;
  margin-bottom: 20px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  margin-top: 60px;
}

.welcome-text p:first-child {
  font-size: 28px;
  margin-bottom: 10px;
  font-weight: bold;
}

.welcome-text p:last-child {
  font-size: 18px;
}

.form-container {
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 20px;
  box-sizing: border-box;
  margin-top: 20px;
}

.login-form {
  width: 100%;
  margin: 0;
  padding: 0;
}

/* 登录输入框容器 */
.login-inputs {
  margin-bottom: 16px;
}

/* 字段容器 */
.field-container {
  margin-bottom: 16px;
}

/* 字段标签 */
.field-label {
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
  text-align: left;
}

/* 登录输入框样式 */
.login-field {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px 16px;
  box-sizing: border-box;
  background: transparent;
}

/* 忘记密码容器 */
.forgot-password-container {
  text-align: right;
  margin-bottom: 16px;
}

.forgot-password {
  font-size: 14px;
  color: #145294;
  text-decoration: none;
}

/* 协议同意容器 */
.agreement-container {
  margin-bottom: 24px;
}

.agreement-checkbox {
  font-size: 14px;
  color: #333;
}

/* 登录按钮容器 */
.login-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 登录按钮样式 */
.login-button {
  height: 48px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
}

/* 短信登录按钮样式 */
.sms-login-button {
  height: 48px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
}

/* 调整验证码输入框按钮区域样式 */
.van-field__button {
  margin-left: 0;
  height: 32px;
  display: flex;
  align-items: center;
}

/* 调整表单样式 */
.van-field {
  background: transparent;
}

.van-field__body {
  padding-right: 5px;
}

.van-checkbox__label {
  color: #333;
}

/* 底部白色区域 */
.login-container::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 67%;
  /* 下半部分为白色 */
  background: #fff;
  z-index: 0;
}

.login-icp {
  position: absolute;
  bottom: 15px;
  left: 0;
  width: 100%;
  text-align: center;
  font-size: 12px;
  z-index: 10;
}

.login-icp a {
  color: #999;
  text-decoration: none;
}
</style>